import React, { useState } from "react";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import CustomAlert from "../../../helpers/CustomAlert";
import { toast } from "react-toastify";
import { MapPin, Ruler, Scale, Package, Truck, CreditCard } from "lucide-react";
import { useSelector } from "react-redux";
import LeftCircle from "../../../assets/Icons/Left circle B.png";
import RightCircle from "../../../assets/Icons/Right circle A.png";
import HeaderWithBreadcrumb from "../../../components/common/HeaderWithBreadcrumb";
import { apiList } from "../../../helpers/apiList";
import { Tooltip } from "@mui/material";

const LMBPRateCalculator = () => {
  const loggedInUserName = sessionStorage.getItem("username");
  const dashboardData = useSelector((state) => state.balance.balance);

  const [formData, setFormData] = useState({
    username: loggedInUserName,
    source_pincode: "",
    destination_pincode: "",
    payment_mode: "PPD",
    express: "",
    shipment_length: "",
    shipment_width: "",
    shipment_height: "",
    shipment_weight: "",
    cod_amount: "",
  });

  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Pincode fields - accept only numbers and limit to 6 digits
    if (name === "source_pincode" || name === "destination_pincode") {
      // Allow only numbers
      const numericValue = value.replace(/[^0-9]/g, "");

      // Limit to 6 digits
      if (numericValue.length > 6) return;

      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));

      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
      return;
    }

    // Numeric fields restriction
    const numericFields = [
      "shipment_length",
      "shipment_width",
      "shipment_height",
      "shipment_weight",
      "cod_amount",
    ];

    if (numericFields.includes(name)) {
      if (!/^\d*\.?\d*$/.test(value)) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleBlurFormat = (name) => {
    if (
      [
        "shipment_length",
        "shipment_width",
        "shipment_height",
        "shipment_weight",
        "cod_amount",
      ].includes(name)
    ) {
      setFormData((prev) => {
        return {
          ...prev,
          [name]: prev[name] ? parseFloat(prev[name]).toFixed(2) : "0.00",
        };
      });

      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  const handleReload = () => {
    setFormData({
      username: loggedInUserName,
      source_pincode: "",
      destination_pincode: "",
      payment_mode: "PPD",
      express: "",
      shipment_length: "",
      shipment_width: "",
      shipment_height: "",
      shipment_weight: "",
      cod_amount: "0.00",
    });
    setErrors({});
    setResult(null);
    setData(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.source_pincode)
      newErrors.source_pincode = "Source pincode is required";
    if (!/^[1-9][0-9]{5}$/.test(formData.source_pincode))
      newErrors.source_pincode = "Enter a valid 6-digit pincode";
    if (!formData.destination_pincode)
      newErrors.destination_pincode = "Destination pincode is required";
    if (!/^[1-9][0-9]{5}$/.test(formData.destination_pincode))
      newErrors.destination_pincode = "Enter a valid 6-digit pincode";
    if (!formData.shipment_weight)
      newErrors.shipment_weight = "Shipment weight is required";
    if (!formData.shipment_length)
      newErrors.shipment_length = "Length is required";
    if (!formData.shipment_width)
      newErrors.shipment_width = "Width is required";
    if (!formData.shipment_height)
      newErrors.shipment_height = "Height is required";
    if (!formData.express) newErrors.express = "Please select Express Type";

    // Minimum value validation (0.1)
    if (formData.shipment_weight && parseFloat(formData.shipment_weight) < 0.1)
      newErrors.shipment_weight = "Shipment weight must be minimum 0.1";
    if (formData.shipment_length && parseFloat(formData.shipment_length) < 0.1)
      newErrors.shipment_length = "Length must be minimum 0.1";
    if (formData.shipment_width && parseFloat(formData.shipment_width) < 0.1)
      newErrors.shipment_width = "Width must be minimum 0.1";
    if (formData.shipment_height && parseFloat(formData.shipment_height) < 0.1)
      newErrors.shipment_height = "Height must be minimum 0.1";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    setErrors(validationErrors);

    // Stop if frontend validation fails
    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    try {
      const payload = {
        username: loggedInUserName,
        sourcePincode: formData?.source_pincode || "",
        destinationPincode: formData?.destination_pincode || "",
        expressType: formData?.express || "",
        weight: parseFloat(formData?.shipment_weight) || 0,
        length: parseFloat(formData?.shipment_length) || 0,
        width: parseFloat(formData?.shipment_width) || 0,
        height: parseFloat(formData?.shipment_height) || 0,
      };

      // Use LMBP specific API endpoint
      const response = await axiosPostApi(
        `${apiList.CHECK_CHARGES}`, // Make sure this is defined in your apiList
        payload
      );

      const apiData = response?.data?.data;

      // ✅ Success condition
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        const transformedData = transformApiResponse(apiData);
        setResult(transformedData);
        setData(apiData);
        toast.success("LMBP Rate calculated successfully!", {
          autoClose: 2000,
        });
      }
      // ⚠️ Backend validation
      else if (apiData?.status === false && apiData?.response) {
        CustomAlert({
          title: "Validation Failed",
          text: apiData.response,
          icon: "warning",
          confirmButtonText: "OK",
        });

        handleReload();
      }
      // ❌ General failure case
      else {
        CustomAlert({
          title: "Failed",
          text: response?.data?.message || "Unable to fetch LMBP rates",
          icon: "error",
        });

        handleReload();
      }
    } catch (err) {
      CustomAlert({
        title: "Error",
        text: "Failed to fetch LMBP rate data. Please try again later.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to transform API response to match UI structure
  const transformApiResponse = (apiData) => {
    // This function should transform your LMBP API response to match the structure expected by the UI
    // You'll need to adjust this based on your actual LMBP API response structure

    // Example transformation (adjust according to your actual API response):
    return {
      source_pincode: apiData?.source?.pincode || "-",
      source_location: apiData.source?.city || "NA",
      destination_pincode:
        apiData?.destination?.pincode || formData.destination_pincode,
      destination_location: apiData.destination?.city || "NA",
      charged_weight: formData?.shipment_weight || "0.00",
      weight_unit: apiData.weight_unit || "kg",
      base_weight: apiData.base_weight || "0.00",
      additional_weight: apiData.additional_weight || "0.00",
      carrierwise_charges: apiData.carrierwise_charges || [
        {
          carrier_name: apiData.carrier_name || "LMBP Express",
          estimated_dd: apiData.estimated_delivery || "3-5 days",
          charges: apiData.total_charges || "0.00",
        },
      ],
      note:
        apiData.note ||
        `*An ${apiData?.gstPercent}% GST is applicable as per government regulations.`,
    };
  };

  return (
    <div>
      <HeaderWithBreadcrumb title="LMBP Rate Calculator" breadcrumb="Master" />

    <div className="bg-[#FFF7ED] border border-[#F3D7B6] rounded-xl px-4 py-3 flex gap-6 text-sm w-fit mb-1">
              <div className="flex items-center gap-2">
                  <span className="text-gray-500 font-medium">
                    Card Type :
                  </span>
                  <span className="text-gray-800 font-semibold">
                    {dashboardData?.wallet?.rateCardName || "N/A"}
                  </span>
                </div>
      </div>
      {/* Main Container */}
      <div className="flex flex-col lg:flex-row bg-white rounded-2xl shadow-lg w-full max-w-[1600px] overflow-hidden border border-gray-200 relative z-10">
        {/* Decorative Circles */}
        <img
          src={LeftCircle}
          alt="Left Decoration"
          className="absolute left-0 bottom-0 w-[110px] h-[105px] z-0"
        />
        <img
          src={RightCircle}
          alt="Right Decoration"
          className="absolute right-0 top-0 w-[110px] h-[105px] z-0"
        />
        {/* LEFT FORM (50%) */}

        <form onSubmit={handleSubmit} className="w-full lg:w-[50%] p-5 md:p-8 lg:p-10 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Source Pincode */}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Source Pincode <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-2 top-2">
                  <MapPin size={16} className="text-[#FF6600]" />
                </div>
                <input
                  type="text"
                  name="source_pincode"
                  placeholder="Enter source pincode"
                  value={formData.source_pincode}
                  onChange={handleChange}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  className={`w-full h-[36px] rounded-md border ${
                    errors.source_pincode ? "border-red-400" : "border-gray-300"
                  } pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white placeholder-gray-400 text-left`}
                />
              </div>
              {errors.source_pincode && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.source_pincode}
                </p>
              )}
            </div>

            {/* Destination Pincode */}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Destination Pincode <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-2 top-2">
                  <MapPin size={16} className="text-[#FF6600]" />
                </div>
                <input
                  type="text"
                  name="destination_pincode"
                  placeholder="Enter destination pincode"
                  value={formData.destination_pincode}
                  onChange={handleChange}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  className={`w-full h-[36px] rounded-md border ${
                    errors.destination_pincode
                      ? "border-red-400"
                      : "border-gray-300"
                  } pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white placeholder-gray-400 text-left`}
                />
              </div>
              {errors.destination_pincode && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.destination_pincode}
                </p>
              )}
            </div>

            {/* Shipment Weight */}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Shipment Weight <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <div className="relative flex-1">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2">
                    <Scale size={16} className="text-[#FF6600]" />
                  </span>
                  <input
                    type="text"
                    name="shipment_weight"
                    placeholder="Enter weight"
                    value={formData.shipment_weight}
                    onChange={handleChange}
                    onBlur={() => handleBlurFormat("shipment_weight")}
                    className={`w-full h-[36px] rounded-md border ${
                      errors.shipment_weight
                        ? "border-red-400"
                        : "border-gray-300"
                    } pl-8 pr-2 text-[15px] text-left focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white placeholder-gray-400`}
                    style={{
                      textAlign: formData.shipment_weight ? "right" : "left",
                    }}
                  />
                </div>
                <div className="bg-[#2e237f] text-white text-[13px] font-medium h-[36px] px-3 flex items-center justify-center rounded-r-md">
                  kg
                </div>
              </div>
              {errors.shipment_weight && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.shipment_weight}
                </p>
              )}
            </div>

            {/* Length */}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Length <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <div className="relative flex-1">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2">
                    <Ruler size={16} className="text-[#FF6600]" />
                  </span>
                  <input
                    type="text"
                    name="shipment_length"
                    placeholder="Enter length"
                    value={formData.shipment_length}
                    onChange={handleChange}
                    onBlur={() => handleBlurFormat("shipment_length")}
                    className={`w-full h-[36px] rounded-md border ${
                      errors.shipment_length
                        ? "border-red-400"
                        : "border-gray-300"
                    } pl-8 pr-2 text-[15px] text-left focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white placeholder-gray-400`}
                    style={{
                      textAlign: formData.shipment_length ? "right" : "left",
                    }}
                  />
                </div>
                <div className="bg-[#2e237f] text-white text-[13px] font-medium h-[36px] px-3 flex items-center justify-center rounded-r-md">
                  cm
                </div>
              </div>
              {errors.shipment_length && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.shipment_length}
                </p>
              )}
            </div>

            {/* Width */}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Width <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <div className="relative flex-1">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2">
                    <Package size={16} className="text-[#FF6600]" />
                  </span>
                  <input
                    type="text"
                    name="shipment_width"
                    placeholder="Enter width"
                    value={formData.shipment_width}
                    onChange={handleChange}
                    onBlur={() => handleBlurFormat("shipment_width")}
                    className={`w-full h-[36px] rounded-md border ${
                      errors.shipment_width
                        ? "border-red-400"
                        : "border-gray-300"
                    } pl-8 pr-2 text-[15px] text-left focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white placeholder-gray-400`}
                    style={{
                      textAlign: formData.shipment_width ? "right" : "left",
                    }}
                  />
                </div>
                <div className="bg-[#2e237f] text-white text-[13px] font-medium h-[36px] px-3 flex items-center justify-center rounded-r-md">
                  cm
                </div>
              </div>
              {errors.shipment_width && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.shipment_width}
                </p>
              )}
            </div>

            {/* Height */}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Height <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <div className="relative flex-1">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2">
                    <Ruler size={16} className="text-[#FF6600]" />
                  </span>
                  <input
                    type="text"
                    name="shipment_height"
                    placeholder="Enter height"
                    value={formData.shipment_height}
                    onChange={handleChange}
                    onBlur={() => handleBlurFormat("shipment_height")}
                    className={`w-full h-[36px] rounded-md border ${
                      errors.shipment_height
                        ? "border-red-400"
                        : "border-gray-300"
                    } pl-8 pr-2 text-[15px] text-left focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white placeholder-gray-400`}
                    style={{
                      textAlign: formData.shipment_height ? "right" : "left",
                    }}
                  />
                </div>
                <div className="bg-[#2e237f] text-white text-[13px] font-medium h-[36px] px-3 flex items-center justify-center rounded-r-md">
                  cm
                </div>
              </div>
              {errors.shipment_height && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.shipment_height}
                </p>
              )}
            </div>

            {/* Payment Mode */}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Payment Mode
              </label>
              <div className="relative">
                <div className="absolute left-2 top-2">
                  <CreditCard size={16} className="text-[#FF6600]" />
                </div>
                <input
                  type="text"
                  name="payment_mode"
                  value={formData.payment_mode}
                  disabled
                  className="w-full h-[36px] rounded-md border border-gray-300 pl-8 pr-2 text-[15px] bg-gray-100 text-gray-800 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Express Type */}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Express Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-2 top-2">
                  <Truck size={16} className="text-[#FF6600]" />
                </div>
                <select
                  name="express"
                  value={formData.express}
                  onChange={handleChange}
                  className={`w-full h-[36px] rounded-md border ${
                    errors.express ? "border-red-400" : "border-gray-300"
                  } pl-8 pr-2 text-[15px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none bg-white text-left text-gray-800`}
                  style={{
                    textAlignLast: "left",
                    paddingLeft: "32px",
                  }}
                >
                  <option value="">Select Express Type</option>
                  <option value="surface">Surface</option>
                  <option value="air">Air</option>
                </select>
              </div>
              {errors.express && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.express}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="col-span-2 flex flex-wrap justify-center gap-4 mt-8">
            {/* Reload Button */}
            <button
              type="button"
              onClick={handleReload}
              className="btn btn-submit !rounded-[8px] w-[133px] h[33px]"
            >
              Reload
            </button>

            {/* Get Charges Button */}
            <button
              type="submit"
              disabled={loading}
              className={`btn btn-submit !rounded-[8px] w-[133px] h[33px] ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#FF6600] hover:bg-[#e25b00] shadow-md"
              }`}
            >
              {loading ? "Loading..." : "Get Charges"}
            </button>
          </div>
        </form>

        {/* RIGHT RESULT (50%) */}
        <div className="w-full lg:w-[50%] bg-[#FFF5EB] p-5 md:p-8 lg:p-12 rounded-l-none rounded-r-2xl border-l border-yellow-300 shadow-inner">
          {!result ? (
            <div className="flex items-center justify-center h-full text-gray-600 text-lg font-medium">
              Fill details and click{" "}
              <strong className="ml-1 text-[#FF6600]">Get Charges</strong>
            </div>
          ) : (
            <div className="space-y-8 text-gray-900">
              {/* Header Row */}
              <div className="flex justify-between items-start gap-4 flex-wrap">
                {/* Source */}
                <div>
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    Source
                  </p>
                  <p className="text-[#00A651] font-bold text-[18px] leading-tight">
                    {result.source_pincode}
                  </p>
                  <p className="text-[14px] text-gray-700 font-medium">
                    {result.source_location}
                  </p>
                </div>

                {/* Truck Icon */}
                <div className="flex justify-center items-center bg-white rounded-full p-3 shadow-md">
                  <Truck className="text-[#FF6600]" size={44} />
                </div>

                {/* Destination */}
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    Destination
                  </p>
                  <p className="text-[#00A651] font-bold text-[18px] leading-tight">
                    {result.destination_pincode}
                  </p>
                  <p className="text-[14px] text-gray-700 font-medium">
                    {result.destination_location}
                  </p>
                </div>
              </div>

              {/* Weight Summary */}
              <div className="flex justify-between items-center text-[15px] border-b border-gray-300 pb-3 flex-wrap gap-3 !mt-2 flex-col">
                <div className="flex justify-between items-center text-[15px] border-b border-gray-300 pb-3 flex-wrap gap-3 !mt-2 w-full">
                <div>
                  <p className="font-semibold text-gray-700">Billing Weight</p>
                  <p className="text-[15px] font-bold text-gray-900">
                    {result.charged_weight} {result.weight_unit}
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-700">Zone</p>
                  <p className="text-[15px] font-bold text-gray-900">
                    {data?.zone}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-700">
                    Additional Weight
                  </p>
                  <p className="text-[15px] font-bold text-gray-900">
                    {data?.extraWeight || 0} {result.weight_unit}
                  </p>
                </div>
                </div>
                <div className="flex between items-center gap-2">
                    <span className="font-semibold text-gray-700">Express Type : </span>
                    <span className="text-[15px] font-bold text-gray-900">{data?.expressType === "surface" ? "Surface" : "Air"}</span>
                </div>
              </div>

              {/* Charges Breakdown */}
                <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200 bg-[#FFF8F1]">
                          <th className="px-5 py-3 text-left text-[13px] font-bold uppercase tracking-wide text-gray-600 whitespace-nowrap">
                            Charges Type
                          </th>
                
                          <th className="px-5 py-3 text-right text-[13px] font-bold uppercase tracking-wide text-gray-600 whitespace-nowrap">
                            Amount
                          </th>
                        </tr>
                      </thead>
                
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="px-5 py-3 text-[15px] text-gray-700 font-medium">
                            Base Amount
                          </td>
                
                          <td className="px-5 py-3 text-[15px] text-gray-900 font-semibold text-right">
                            ₹ {Number(data?.baseAmount || 0).toFixed(2)}
                          </td>
                        </tr>

                        {/* <Tooltip
                            title={`Additional Weight (${data?.extraWeight || 0} ${result.weight_unit}) x ₹ ${Number(data?.additionalRate || 0).toFixed(2)} per ${result.weight_unit}`}
                            arrow
                            placement="top"
                        > */}
                        <tr className="border-b border-gray-100">
                          <td className="px-5 py-3 text-[15px] text-gray-700 font-medium">
                            Additional Weight Charges
                          </td>
                
                          <td className="px-5 py-3 text-[15px] text-gray-900 font-semibold text-right">
                            ₹ {Number(data?.additionalChargeAmount|| 0).toFixed(2)}
                          </td>
                        </tr>
                        {/* </Tooltip> */}
                
                        <tr className="border-b border-gray-100">
                          <td className="px-5 py-3 text-[15px] text-gray-700 font-medium">
                            Fuel Charges
                          </td>
                
                          <td className="px-5 py-3 text-[15px] text-gray-900 font-semibold text-right">
                            ₹ {Number(data?.fuelCharges || 0).toFixed(2)}
                          </td>
                        </tr>

                        {/* <tr className="border-b border-gray-100">
                           <td className="px-5 py-3 text-[15px] text-gray-700 font-medium">
                             Additional Charges (Per KG)
                           </td>
                       
                           <td className="px-5 py-3 text-[15px] text-gray-900 font-semibold text-right">
                             ₹ {Number(data?.additionalRate || 0).toFixed(2)}
                           </td>
                         </tr> */}


                        {/* <tr className="border-b border-gray-100">
                          <td className="px-5 py-3 text-[15px] text-gray-700 font-medium">
                            Final Amount
                          </td>
                
                          <td className="px-5 py-3 text-[15px] text-gray-900 font-semibold text-right">
                            ₹ {Number(data?.finalAmount || 0).toFixed(2)}
                          </td>
                        </tr> */}
                
                        <tr className="border-b border-gray-100">
                          <td className="px-5 py-3 text-[15px] text-gray-700 font-medium">
                            GST ({Number(data?.gstPercent || 0).toFixed(0)}%)
                          </td>
                
                          <td className="px-5 py-3 text-[15px] text-gray-900 font-semibold text-right">
                            ₹ {Number(data?.gstAmount || 0).toFixed(2)}
                          </td>
                        </tr>
                
                        {/* <Tooltip
                            title={`Final Amount + GST`}
                            arrow
                            placement="top"
                        > */}
                        <tr className="bg-[#FFF8F1]">
                          <td className="px-5 py-3 text-[16px] text-gray-900 font-bold">
                            Total Charges
                          </td>
                
                          <td className="px-5 py-3 text-[18px] text-[#111827] font-bold text-right">
                            ₹ {Number(data?.totalCharges || 0).toFixed(2)}
                          </td>
                        </tr>
                        {/* </Tooltip> */}
                      </tbody>
                    </table>
                  </div>
                
                  {/* Mobile Card View */}
                  <div className="block md:hidden divide-y divide-gray-100">
                    {[
                      {
                        label: "Base Amount",
                        value: Number(data?.baseAmount || 0).toFixed(2),
                      },
                      {
                        label: "Fuel Charges",
                        value: Number(data?.fuelCharges || 0).toFixed(2),
                      },
                      {
                        label: `GST (${Number(data?.gstPercent || 0).toFixed(0)}%)`,
                        value: Number(data?.gstAmount || 0).toFixed(2),
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between px-4 py-3"
                      >
                        <p className="text-[14px] text-gray-600 font-medium">
                          {item.label}
                        </p>
                
                        <p className="text-[15px] text-gray-900 font-semibold text-right">
                          ₹ {item.value}
                        </p>
                      </div>
                    ))}
                
                    {/* Total */}
                    <div className="flex items-center justify-between px-4 py-3 bg-[#FFF8F1]">
                      <p className="text-[15px] text-gray-900 font-bold">
                        Total Charges
                      </p>
                
                      <p className="text-[18px] text-gray-900 font-bold text-right">
                        ₹ {Number(data?.totalCharges || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

              {/* Note */}
              <p className="text-[13px] text-gray-500 mt-3 italic">
                {result.note}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LMBPRateCalculator;
